import { useNavigate } from 'react-router-dom';
import { STEP_ORDER, useProgress, type StepKey } from '../state/ProgressContext';
import { positionForRole } from '../content/positions';
import { workflowFor } from '../content/outreachMethods';

/**
 * A bubble stands for every onboarding step up to the next bubble shown (e.g. "First Lead
 * Training" covers all the training steps). It is complete only when all of those are done, and
 * it is the current bubble while the user's furthest unlocked step is one of them - so a user
 * part-way through training sees Training as current, not as already done.
 */
export function headerStepState(stepKey: StepKey, shownKeys: StepKey[], highestUnlockedStepIndex: number) {
  const index = STEP_ORDER.indexOf(stepKey);
  const position = shownKeys.indexOf(stepKey);
  const nextShown = shownKeys.slice(position + 1).map((key) => STEP_ORDER.indexOf(key)).find((value) => value > index);
  const end = nextShown ?? STEP_ORDER.length;
  const isLast = nextShown === undefined;
  const complete = isLast ? highestUnlockedStepIndex >= STEP_ORDER.length - 1 && stepKey === STEP_ORDER[STEP_ORDER.length - 1] : highestUnlockedStepIndex >= end;
  const current = !complete && highestUnlockedStepIndex >= index && highestUnlockedStepIndex < end;
  return { complete, current };
}

/** Short names for each training step, shown under its bubble. */
export const TRAINING_STEP_LABELS: Record<string, string> = {
  HVAC_PRODUCT: 'Product',
  HVAC_SELLING: 'Selling',
  HVAC_DEMO: 'Demo',
  HVAC_LEADS_REPORTING: 'Leads & feedback',
  REMOTE_LEAD_IMPORTED: 'Get a lead',
  REMOTE_VOIP_ISSUED: 'Calling line',
  REMOTE_CALL_LOGGED: 'Make the call',
  REMOTE_NOTES_COMPLETED: 'Notes',
  REMOTE_EMAIL_CONSTRUCTOR_USED: 'Follow-up email',
  FIELD_LEAD_IMPORTED: 'Claim a business',
  FIELD_LEAD_VISITED: 'Visit',
  FIELD_NOTES_COMPLETED: 'Notes',
  FIELD_EMAIL_CONSTRUCTOR_USED: 'Follow-up email',
  CLAIM_LEAD: 'Get a lead',
  OUTREACH_DRAFT_SAVED: 'Draft',
  INITIAL_EMAIL_SENT: 'Send email',
  ADMIN_FEEDBACK_COMPLETE: 'Feedback',
  FOLLOW_UP_COMPLETED: 'Follow-up',
};

/** HVAC training (it has the HVAC modules) calls the notes step Feedback - there are no notes. */
export function trainingStepLabel(key: string, steps: string[]) {
  if (key.endsWith('_NOTES_COMPLETED') && steps.includes('HVAC_PRODUCT')) return 'Feedback';
  return TRAINING_STEP_LABELS[key] ?? key;
}

/**
 * One training step's bubble, from the user's actual training progress (the same record the
 * training page reads): done steps are complete, the step they are on now is current.
 */
export function trainingStepState(
  key: string,
  training: { approvedAt: string | null; currentStepKey: string | null; completedStepKeys: string[] },
  trainingUnlocked: boolean,
) {
  if (training.approvedAt) return { complete: true, current: false };
  if (!trainingUnlocked) return { complete: false, current: false };
  const complete = training.completedStepKeys.includes(key);
  return { complete, current: !complete && training.currentStepKey === key };
}

interface HeaderItem {
  key: string;
  label: string;
  caption?: string;
  route: string;
  unlocked: boolean;
  complete: boolean;
  current: boolean;
  failed: boolean;
}

export function ProgressHeader() {
  const { progress } = useProgress();
  const navigate = useNavigate();
  if (!progress) return null;
  const groups = workflowFor(positionForRole(progress.roleId), progress.outreachMethod);
  const shownKeys = groups.flatMap((group) => group.steps.map((step) => step.key));
  const trainingIndex = STEP_ORDER.indexOf('ONBOARDING_WELCOME');
  const trainingUnlocked = trainingIndex <= progress.highestUnlockedStepIndex;
  const taxStatus = progress.country?.code === 'US' ? progress.documents.w9.status : progress.documents.w8ben.status;

  const itemsFor = (steps: (typeof groups)[number]['steps']): HeaderItem[] =>
    steps.flatMap((step): HeaderItem[] => {
      // Training is one page and one bubble: the bubble is current until every training step is
      // done (the steps themselves are on the training page), and Active is not a bubble of its own.
      if (step.key === 'COMPLETE') return [];
      if (step.key === 'ONBOARDING_WELCOME')
        return [{
          key: step.key,
          label: step.label,
          route: step.route,
          unlocked: trainingUnlocked,
          failed: false,
          complete: Boolean(progress.training.approvedAt),
          current: trainingUnlocked && !progress.training.approvedAt,
        }];
      const index = STEP_ORDER.indexOf(step.key);
      const applicationFailed = ['REJECTED', 'RETRACTED'].includes(progress.application.status)
        && (step.key === 'APPLICATION_QUESTIONS' || step.key === 'APPLICATION_REVIEW');
      const documentsFailed = step.key === 'AGREEMENT'
        ? progress.documents.agreement.status === 'DENIED'
        : (step.key === 'TAX_FORM' || step.key === 'DOCUMENT_REVIEW') && taxStatus === 'DENIED';
      return [{
        key: step.key,
        label: step.label,
        route: step.route,
        unlocked: index <= progress.highestUnlockedStepIndex,
        failed: applicationFailed || documentsFailed,
        ...headerStepState(step.key, shownKeys, progress.highestUnlockedStepIndex),
      }];
    });

  return (
    <>
    {progress.outreachMethodLabel && <p className="progress-outreach-method">Outreach Method: <strong>{progress.outreachMethodLabel}</strong></p>}
    <nav className="progress-header grouped-progress" aria-label="Onboarding progress">
      {groups.map((group) => (
        <section className="progress-group" key={group.name}>
          <h2>{group.name}</h2>
          <ol>
            {itemsFor(group.steps).map((item, localIndex) => (
              <li key={item.key}>
                <button
                  type="button"
                  title={item.label}
                  aria-label={`${group.name}: ${item.label}${item.current ? ' (current step)' : item.complete ? ' (done)' : ''}`}
                  aria-current={item.current ? 'step' : undefined}
                  disabled={!item.unlocked}
                  className={item.failed ? 'failed' : item.complete ? 'completed' : item.current ? 'current' : undefined}
                  onClick={() => item.unlocked && navigate(item.route)}
                >
                  <span>{localIndex + 1}</span>
                </button>
                {item.caption && <small className={`training-step-caption${item.current ? ' current' : ''}`}>{item.caption}</small>}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </nav>
    </>
  );
}
